/**
 * Local IndexedDB store for multiple drawings.
 * Serves as local cache and offline support.
 */
import { createStore, get, set, del, entries } from "idb-keyval";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

const drawingsStore = createStore("drawings-db", "drawings-store");

const ACTIVE_DRAWING_KEY = "excalidraw-active-drawing-id";

export interface SavedDrawing {
  id: string;
  name: string;
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles;
  createdAt: number;
  updatedAt: number;
  thumbnail?: string;
  syncStatus: "synced" | "local" | "pending";
}

export interface DrawingListItem {
  id: string;
  name: string;
  thumbnail?: string;
  updatedAt: number;
  syncStatus: "synced" | "local" | "pending";
}

export const DrawingStore = {
  async save(drawing: SavedDrawing): Promise<void> {
    await set(drawing.id, drawing, drawingsStore);
  },

  async load(id: string): Promise<SavedDrawing | undefined> {
    return get<SavedDrawing>(id, drawingsStore);
  },

  async list(): Promise<DrawingListItem[]> {
    const allEntries = await entries<string, SavedDrawing>(drawingsStore);
    return allEntries
      .map(([, drawing]) => ({
        id: drawing.id,
        name: drawing.name,
        thumbnail: drawing.thumbnail,
        updatedAt: drawing.updatedAt,
        syncStatus: drawing.syncStatus,
      }))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  },

  async delete(id: string): Promise<void> {
    await del(id, drawingsStore);
  },

  getActiveDrawingId(): string | null {
    return localStorage.getItem(ACTIVE_DRAWING_KEY);
  },

  setActiveDrawingId(id: string | null): void {
    if (id) {
      localStorage.setItem(ACTIVE_DRAWING_KEY, id);
    } else {
      localStorage.removeItem(ACTIVE_DRAWING_KEY);
    }
  },
};
